import React, { useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Typography,
    TableContainer,
    Chip,
    Select,
    Box,
    MenuItem,
    FormControl,
    TextField,
    SelectChangeEvent,
} from '@mui/material';
import * as XLSX from 'xlsx';
import developmentsData from '../../resident-portal';
import { useAppDispatch } from '../../redux/hooks';
import { residentPortalApi, useGetPropertyInvoicesQuery } from '../../redux/residentPortalApi';

type InvoiceFilters = {
    invoiceNumber: string;
    paidStatus: string;
    development: string;
    phase: string;
    type: string;
    dueDate: string;
    unitNumber: string;
};

const formatOptionLabel = (value: string) =>
    value
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const PropertyInvoice: React.FC = () => {
    const dispatch = useAppDispatch();
    const propertyMetrics = developmentsData.propertyMetrics ?? [];
    const [filters, setFilters] = useState<InvoiceFilters>({
        invoiceNumber: '',
        paidStatus: '',
        development: '',
        phase: '',
        type: '',
        dueDate: '',
        unitNumber: '',
    });
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [isExporting, setIsExporting] = useState(false);

    const developmentOptions = Array.from(new Set(propertyMetrics.map((metric) => metric.development)));
    const phaseOptions = Array.from(
        new Set(
            propertyMetrics
                .filter((metric) => !filters.development || metric.development === filters.development)
                .map((metric) => metric.phase),
        ),
    );
    const typeOptions = Array.from(
        new Set(
            propertyMetrics
                .filter((metric) => {
                    const matchesDevelopment = !filters.development || metric.development === filters.development;
                    const matchesPhase = !filters.phase || metric.phase === filters.phase;
                    return matchesDevelopment && matchesPhase;
                })
                .map((metric) => metric.type),
        ),
    );

    const { data: invoices = [], isFetching } = useGetPropertyInvoicesQuery({
        invoiceNumber: filters.invoiceNumber,
        paid: filters.paidStatus,
        development: filters.development,
        phase: filters.phase,
        type: filters.type,
        dueDate: filters.dueDate,
        unitNumber: filters.unitNumber,
        limit: pageSize,
        skip: page * pageSize,
    });

    const isDueDatePassed = (dueDate: string) => new Date(dueDate) < new Date();

    const handleFilterChange = (field: keyof InvoiceFilters, value: string) => {
        setPage(0);
        setFilters((prevFilters) => {
            if (field === 'development') {
                return {
                    ...prevFilters,
                    development: value,
                    phase: '',
                    type: '',
                };
            }

            if (field === 'phase') {
                return {
                    ...prevFilters,
                    phase: value,
                    type: '',
                };
            }

            return {
                ...prevFilters,
                [field]: value,
            };
        });
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
        const newSize = Number(event.target.value);
        setPageSize(newSize);
        setPage(0);
    };

    const handleExport = async () => {
        setIsExporting(true);

        try {
            const rows = await dispatch(
                residentPortalApi.endpoints.getPropertyInvoices.initiate(
                    {
                        invoiceNumber: filters.invoiceNumber,
                        paid: filters.paidStatus,
                        development: filters.development,
                        phase: filters.phase,
                        type: filters.type,
                        dueDate: filters.dueDate,
                        unitNumber: filters.unitNumber,
                        limit: -1,
                        skip: 0,
                    },
                    { subscribe: false, forceRefetch: true },
                ),
            ).unwrap();

            if (!rows?.length) {
                window.alert('No invoices found for the selected filters.');
                return;
            }

            const worksheet = XLSX.utils.json_to_sheet(
                rows.map((invoice) => ({
                    'Invoice Number': invoice.invoiceNumber,
                    Development: invoice.invoice?.development ?? invoice.property?.development ?? '',
                    Phase: invoice.invoice?.phase ?? invoice.property?.phase ?? '',
                    Type: invoice.invoice?.type ?? invoice.property?.type ?? '',
                    'Unit Number': invoice.property?.unitNumber ?? '',
                    'Invoice Amount': invoice.invoice?.totalCost ?? 0,
                    'Invoice Balance': invoice.propertyInvoiceBalance ?? 0,
                    'Due Date': invoice.invoice?.dueDate
                        ? new Date(invoice.invoice.dueDate).toLocaleDateString()
                        : '',
                    Status: invoice.status ?? '',
                })),
            );

            worksheet['!cols'] = [
                { wch: 18 },
                { wch: 20 },
                { wch: 12 },
                { wch: 16 },
                { wch: 12 },
                { wch: 16 },
                { wch: 16 },
                { wch: 16 },
                { wch: 16 },
            ];

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Property Invoices');
            XLSX.writeFile(workbook, `property-invoices-${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            console.error('Failed to export property invoices', error);
            window.alert('Failed to export the invoice report. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <Box sx={{ padding: 3, borderRadius: 2, marginBottom: 3, backgroundColor: '#f9f9f9', boxShadow: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
                    Property Invoices
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: 2, color: '#666' }}>
                    Filter the invoice list and export all matching rows to Excel.
                </Typography>
                <Box display="flex" gap={2} alignItems="center" width="100%" flexWrap="wrap">
                    <TextField
                        label="Invoice Number"
                        variant="outlined"
                        size="small"
                        value={filters.invoiceNumber}
                        onChange={(e) => handleFilterChange('invoiceNumber', e.target.value)}
                        sx={{ flex: '1 1 180px', backgroundColor: '#fff', borderRadius: 1 }}
                    />
                    <TextField
                        select
                        label="Status"
                        variant="outlined"
                        size="small"
                        value={filters.paidStatus}
                        onChange={(e) => handleFilterChange('paidStatus', e.target.value)}
                        sx={{ flex: '1 1 180px', backgroundColor: '#fff', borderRadius: 1 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Partially Paid">Partially Paid</MenuItem>
                        <MenuItem value="Unpaid">Unpaid</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Development"
                        variant="outlined"
                        size="small"
                        value={filters.development}
                        onChange={(e) => handleFilterChange('development', e.target.value)}
                        sx={{ flex: '1 1 180px', backgroundColor: '#fff', borderRadius: 1 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {developmentOptions.map((development) => (
                            <MenuItem key={development} value={development}>
                                {formatOptionLabel(development)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Phase"
                        variant="outlined"
                        size="small"
                        value={filters.phase}
                        onChange={(e) => handleFilterChange('phase', e.target.value)}
                        disabled={!filters.development}
                        sx={{ flex: '1 1 180px', backgroundColor: '#fff', borderRadius: 1 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {phaseOptions.map((phase) => (
                            <MenuItem key={phase} value={phase}>
                                {formatOptionLabel(phase)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label="Type"
                        variant="outlined"
                        size="small"
                        value={filters.type}
                        onChange={(e) => handleFilterChange('type', e.target.value)}
                        disabled={!filters.phase}
                        sx={{ flex: '1 1 180px', backgroundColor: '#fff', borderRadius: 1 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {typeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                                {formatOptionLabel(type)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Unit Number"
                        variant="outlined"
                        size="small"
                        value={filters.unitNumber}
                        onChange={(e) => handleFilterChange('unitNumber', e.target.value)}
                        sx={{ flex: '1 1 180px', backgroundColor: '#fff', borderRadius: 1 }}
                    />
                    <TextField
                        label="Due Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={filters.dueDate}
                        onChange={(e) => handleFilterChange('dueDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: '1 1 180px', backgroundColor: '#fff', borderRadius: 1 }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExport}
                        disabled={isExporting || isFetching}
                        sx={{ minWidth: 180, height: 40 }}
                    >
                        {isExporting ? 'Exporting...' : 'Export Excel'}
                    </Button>
                </Box>

            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#1976d2' }}>
                        <TableRow>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice Number</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Property</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Unit Number</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice Amount</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice Balance</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
                            <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoices.length ? invoices.map((invoice) => (
                            <TableRow key={invoice.id}>
                                <TableCell>{invoice.invoiceNumber}</TableCell>
                                <TableCell>
                                    {invoice.property?.development} {invoice.property?.type} {invoice.property?.unitNumber}
                                </TableCell>
                                <TableCell>{invoice.property?.unitNumber}</TableCell>
                                <TableCell>${invoice.invoice?.totalCost ?? 0}</TableCell>
                                <TableCell>${invoice?.propertyInvoiceBalance ?? 0}</TableCell>
                                <TableCell>
                                    {invoice.invoice?.dueDate ? (
                                        <Typography
                                            style={{
                                                color: invoice.status === 'Paid'
                                                    ? '#4CAF50'
                                                    : isDueDatePassed(invoice.invoice.dueDate)
                                                        ? 'lightcoral'
                                                        : '#F7C914',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {new Date(invoice.invoice.dueDate).toLocaleDateString()}
                                        </Typography>
                                    ) : (
                                        <Typography>Not Available</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={
                                            invoice?.status === 'Paid'
                                                ? 'Paid'
                                                : invoice?.status === 'Unpaid'
                                                    ? 'Unpaid'
                                                    : invoice?.status === 'Cancelled'
                                                        ? 'Cancelled'
                                                        : 'Partially Paid'
                                        }
                                        style={{
                                            backgroundColor:
                                                invoice?.status === 'Paid'
                                                    ? '#4CAF50'
                                                    : invoice?.status === 'Unpaid'
                                                        ? '#f44336'
                                                        : invoice?.status === 'Cancelled'
                                                            ? '#6b7280'
                                                            : '#FFBF00',
                                            color: '#fff',
                                        }}
                                    />
                                </TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    <Typography sx={{ py: 2, color: '#666' }}>
                                        {isFetching ? 'Loading invoices...' : 'No invoices found for the selected filters.'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                    <Button
                        onClick={() => handlePageChange(Math.max(page - 1, 0))}
                        disabled={page === 0}
                        variant="contained"
                        sx={{ marginRight: 2 }}
                    >
                        Previous
                    </Button>
                    <Typography variant="body1">Page {page + 1}</Typography>
                    <Button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={invoices.length < pageSize}
                        variant="contained"
                        sx={{ marginLeft: 2 }}
                    >
                        Next
                    </Button>
                </Box>
                <Box mt={2} display="flex" justifyContent="center" alignItems="center">
                    <Typography variant="body2" sx={{ marginRight: 2 }}>Rows per page:</Typography>
                    <FormControl variant="outlined" size="small">
                        <Select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            sx={{ backgroundColor: '#f0f4f7' }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </TableContainer>
        </>
    );
};

export default PropertyInvoice;
